import React from 'react';
import Content from '../content';
import * as styles from './PageLayout.module.css';

interface PageLayoutProps {
  children: React.ReactNode;
  container?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  background?:
    | 'default'
    | 'riche'
    | 'wb'
    | 'ozon'
    | 'yandex'
    | 'dark'
    | 'light';
}

interface PageSectionProps {
  children: React.ReactNode;
  fullWidth?: boolean;
  className?: string;
}

interface PageHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface PageContentProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Гибкий компонент для создания страниц с комбинацией ограниченного и полноширинного контента
 *
 * @example
 * <PageLayout container="md">
 *   <PageLayout.Header>
 *     <h1>Заголовок страницы</h1>
 *     <Tabs />
 *   </PageLayout.Header>
 *
 *   <PageLayout.Section>
 *     <h2>Заголовок секции</h2>
 *     <Button>Фильтр</Button>
 *   </PageLayout.Section>
 *
 *   <PageLayout.Section fullWidth>
 *     <Table />
 *   </PageLayout.Section>
 * </PageLayout>
 */
export default function PageLayout({
  children,
  container = 'md',
  background = 'default',
}: PageLayoutProps) {
  return (
    <Content container={container} background={background}>
      {children}
    </Content>
  );
}

/**
 * Секция страницы - может быть ограниченной или полноширинной
 */
function PageSection({
  children,
  fullWidth = false,
  className = '',
}: PageSectionProps) {
  if (fullWidth) {
    return (
      <div className={`${styles.fullWidthSection} ${className}`}>
        {children}
      </div>
    );
  }

  return <div className={`${styles.section} ${className}`}>{children}</div>;
}

/**
 * Заголовок страницы - всегда в ограниченном контейнере
 */
function PageHeader({ children, className = '' }: PageHeaderProps) {
  return <div className={`${styles.header} ${className}`}>{children}</div>;
}

/**
 * Основной контент страницы - всегда в ограниченном контейнере
 */
function PageContent({ children, className = '' }: PageContentProps) {
  return <div className={`${styles.content} ${className}`}>{children}</div>;
}

// Прикрепляем подкомпоненты к основному компоненту
PageLayout.Header = PageHeader;
PageLayout.Section = PageSection;
PageLayout.Content = PageContent;

export { PageLayout };
